/**
 * Spot WebSocket Market Streams
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketStreamsBase, WebsocketStream, createStreamHandler } from '@binance/common';
import { Api } from './modules/api';

import type { UserDataStreamEventsResponse } from './types';

import type {
    AggTradeRequest,
    AllMarketRollingWindowTickerRequest,
    AllMiniTickerRequest,
    AvgPriceRequest,
    BlockTradeRequest,
    BookTickerRequest,
    DiffBookDepthRequest,
    KlineRequest,
    KlineOffsetRequest,
    MiniTickerRequest,
    PartialBookDepthRequest,
    ReferencePriceRequest,
    RollingWindowTickerRequest,
    TickerRequest,
    TradeRequest,
} from './modules/api';

import type {
    AggTradeResponse,
    AllMarketRollingWindowTickerResponse,
    AllMiniTickerResponse,
    AvgPriceResponse,
    BlockTradeResponse,
    BookTickerResponse,
    DiffBookDepthResponse,
    KlineResponse,
    KlineOffsetResponse,
    MiniTickerResponse,
    PartialBookDepthResponse,
    ReferencePriceResponse,
    RollingWindowTickerResponse,
    TickerResponse,
    TradeResponse,
} from './types';

export class WebsocketStreamsConnection {
    private websocketBase: WebsocketStreamsBase;
    private api: Api;

    constructor(websocketBase: WebsocketStreamsBase) {
        this.websocketBase = websocketBase;
        this.api = new Api(websocketBase);
    }

    /**
     * Adds an event listener for the specified WebSocket event.
     * @param event - The WebSocket event to listen for, such as 'open', 'message', 'error', 'close', 'ping', or 'pong'.
     * @param listener - The callback function to be executed when the event is triggered. The function can accept any number of arguments.
     */
    on(
        event: 'open' | 'message' | 'error' | 'close' | 'ping' | 'pong',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listener: (...args: any[]) => void
    ) {
        this.websocketBase.on(event, listener);
    }

    /**
     * Removes an event listener for the specified WebSocket event.
     * @param event - The WebSocket event to stop listening for, such as 'open', 'message', 'error', 'close', 'ping', or 'pong'.
     * @param listener - The callback function that was previously added as the event listener.
     */
    off(
        event: 'open' | 'message' | 'error' | 'close' | 'ping' | 'pong',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listener: (...args: any[]) => void
    ) {
        this.websocketBase.off(event, listener);
    }

    /**
     * Disconnects from the WebSocket server.
     * If there is no active connection, a warning is logged.
     * Otherwise, all connections in the connection pool are closed gracefully,
     * and a message is logged indicating that the connection has been disconnected.
     * @returns A Promise that resolves when all connections have been closed.
     * @throws Error if the WebSocket client is not set.
     */
    disconnect(): Promise<void> {
        return this.websocketBase.disconnect();
    }

    /**
     * Checks if the WebSocket connection is currently open.
     * @returns `true` if the connection is open, `false` otherwise.
     */
    isConnected(): boolean {
        return this.websocketBase.isConnected();
    }

    /**
     * Sends a ping message to all connected Websocket servers in the pool.
     * If no connections are ready, a warning is logged.
     * For each active connection, the ping message is sent, and debug logs provide details.
     * @throws Error if a Websocket client is not set for a connection.
     */
    pingServer(): void {
        this.websocketBase.pingServer();
    }

    /**
     * Subscribes to one or multiple WebSocket streams
     * Handles both single and pool modes
     * @param stream Single stream name or array of stream names to subscribe to
     * @param id Optional subscription ID
     * @returns void
     */
    subscribe(stream: string | string[], id?: string): void {
        this.websocketBase.subscribe(stream, id);
    }

    /**
     * Unsubscribes from one or multiple WebSocket streams
     * Handles both single and pool modes
     * @param stream Single stream name or array of stream names to unsubscribe from
     * @param id Optional unsubscription ID
     * @returns void
     */
    unsubscribe(stream: string | string[], id?: string): void {
        this.websocketBase.unsubscribe(stream, id);
    }

    /**
     * Checks if the WebSocket connection is subscribed to the specified stream.
     * @param stream The name of the WebSocket stream to check.
     * @returns `true` if the connection is subscribed to the stream, `false` otherwise.
     */
    isSubscribed(stream: string): boolean {
        return this.websocketBase.isSubscribed(stream);
    }

    /**
     * Subscribes to the user data WebSocket stream using the provided listen key.
     * @param listenKey - The listen key for the user data WebSocket stream.
     * @param id - Optional user data stream ID
     * @returns A WebSocket stream handler for the user data stream.
     */
    userData(listenKey: string, id?: string): WebsocketStream<UserDataStreamEventsResponse> {
        return createStreamHandler<UserDataStreamEventsResponse>(this.websocketBase, listenKey, id);
    }

    /**
     * The Aggregate Trade Streams push trade information that is aggregated
     * for a single taker order.
     *
     * Update Speed: Real-time
     *
     * @summary Aggregate Trade Streams
     * @param {AggTradeRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AggTradeResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#agg-trade Binance API Documentation}
     */
    aggTrade(requestParameters: AggTradeRequest): WebsocketStream<AggTradeResponse> {
        return this.api.aggTrade(requestParameters);
    }

    /**
     * Rolling window ticker statistics for all market symbols, computed over
     * multiple windows.
     *
     * Note that only tickers that have changed will be present in the array.
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Rolling Window Statistics Streams
     * @param {AllMarketRollingWindowTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllMarketRollingWindowTickerResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#all-market-rolling-window-ticker Binance API Documentation}
     */
    allMarketRollingWindowTicker(
        requestParameters: AllMarketRollingWindowTickerRequest
    ): WebsocketStream<AllMarketRollingWindowTickerResponse> {
        return this.api.allMarketRollingWindowTicker(requestParameters);
    }

    /**
     * 24hr rolling window mini-ticker statistics for all symbols that changed
     * in an array. These are NOT the statistics of the UTC day, but a 24hr
     * rolling window for the previous 24hrs. Note that only tickers that have
     * changed will be present in the array.
     *
     * Update Speed: 1000ms
     *
     * @summary All Market Mini Tickers Stream
     * @param {AllMiniTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AllMiniTickerResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#all-mini-ticker Binance API Documentation}
     */
    allMiniTicker(
        requestParameters: AllMiniTickerRequest = {}
    ): WebsocketStream<AllMiniTickerResponse> {
        return this.api.allMiniTicker(requestParameters);
    }

    /**
     * Average price streams push changes in the average price over a fixed time interval.
     *
     * Update Speed: 1000ms
     *
     * @summary Average Price
     * @param {AvgPriceRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<AvgPriceResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#avg-price Binance API Documentation}
     */
    avgPrice(requestParameters: AvgPriceRequest): WebsocketStream<AvgPriceResponse> {
        return this.api.avgPrice(requestParameters);
    }

    /**
     * Block Trade Streams push block trade information in real-time.
     *
     * Update Speed: Real-time
     *
     * @summary Block Trade Streams
     * @param {BlockTradeRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<BlockTradeResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#block-trade Binance API Documentation}
     */
    blockTrade(requestParameters: BlockTradeRequest): WebsocketStream<BlockTradeResponse> {
        return this.api.blockTrade(requestParameters);
    }

    /**
     * Pushes any update to the best bid or ask's price or quantity in
     * real-time for a specified symbol.
     *
     * Multiple `<symbol>@bookTicker` streams can be subscribed to over one
     * connection.
     *
     * Update Speed: Real-time
     *
     * @summary Individual Symbol Book Ticker Streams
     * @param {BookTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<BookTickerResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#book-ticker Binance API Documentation}
     */
    bookTicker(requestParameters: BookTickerRequest): WebsocketStream<BookTickerResponse> {
        return this.api.bookTicker(requestParameters);
    }

    /**
     * Order book price and quantity depth updates used to locally manage an order book.
     *
     * Update Speed: 1000ms or 100ms
     *
     * @summary Diff. Depth Stream
     * @param {DiffBookDepthRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<DiffBookDepthResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#diff-book-depth Binance API Documentation}
     */
    diffBookDepth(requestParameters: DiffBookDepthRequest): WebsocketStream<DiffBookDepthResponse> {
        return this.api.diffBookDepth(requestParameters);
    }

    /**
     * The Kline/Candlestick Stream push updates to the current
     * klines/candlestick every second in `UTC+0` timezone
     *
     * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
     *
     * @summary Kline/Candlestick Streams for UTC
     * @param {KlineRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<KlineResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#kline Binance API Documentation}
     */
    kline(requestParameters: KlineRequest): WebsocketStream<KlineResponse> {
        return this.api.kline(requestParameters);
    }

    /**
     * The Kline/Candlestick Stream push updates to the current
     * klines/candlestick every second in `UTC+8` timezone
     *
     **Kline/Candlestick chart intervals:**
     *
     * Supported intervals: See Kline/Candlestick chart intervals
     *
     **UTC+8 timezone offset:**
     * - Kline intervals open and close in the UTC+8 timezone. For example the 1d klines will open at the beginning of the UTC+8 day, and close at the end of the UTC+8 day.
     * - Note that E (event time), t (start time) and T (close time) in the payload are Unix timestamps, which are always interpreted in UTC.
     *
     * Update Speed: 1000ms for `1s`, 2000ms for the other intervals
     *
     * @summary Kline/Candlestick Streams with timezone offset
     * @param {KlineOffsetRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<KlineOffsetResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#kline-offset Binance API Documentation}
     */
    klineOffset(requestParameters: KlineOffsetRequest): WebsocketStream<KlineOffsetResponse> {
        return this.api.klineOffset(requestParameters);
    }

    /**
     * 24hr rolling window mini-ticker statistics. These are NOT the statistics
     * of the UTC day, but a 24hr rolling window for the previous 24hrs.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Mini Ticker Stream
     * @param {MiniTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<MiniTickerResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#mini-ticker Binance API Documentation}
     */
    miniTicker(requestParameters: MiniTickerRequest): WebsocketStream<MiniTickerResponse> {
        return this.api.miniTicker(requestParameters);
    }

    /**
     * Top **\<levels\>** bids and asks, pushed every second.
     *
     * Update Speed: 1000ms or 100ms
     *
     * @summary WebSocket Partial Book Depth Streams
     * @param {PartialBookDepthRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<PartialBookDepthResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#partial-book-depth Binance API Documentation}
     */
    partialBookDepth(
        requestParameters: PartialBookDepthRequest
    ): WebsocketStream<PartialBookDepthResponse> {
        return this.api.partialBookDepth(requestParameters);
    }

    /**
     * Reference price stream for a symbol.
     *
     * Update Speed: 1000ms
     *
     * @summary Reference Price Streams
     * @param {ReferencePriceRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<ReferencePriceResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#reference-price Binance API Documentation}
     */
    referencePrice(
        requestParameters: ReferencePriceRequest
    ): WebsocketStream<ReferencePriceResponse> {
        return this.api.referencePrice(requestParameters);
    }

    /**
     * Rolling window ticker statistics for a single symbol, computed over
     * multiple windows.
     *
     **Note:** This stream is different from the `<symbol>@ticker` stream. The open time `"O"` always starts on a minute, while the closing time `"C"` is the current time
     * of the update. As such, the effective window might be up to 59999ms wider than `<window_size>`.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Rolling Window Statistics Streams
     * @param {RollingWindowTickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<RollingWindowTickerResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#rolling-window-ticker Binance API Documentation}
     */
    rollingWindowTicker(
        requestParameters: RollingWindowTickerRequest
    ): WebsocketStream<RollingWindowTickerResponse> {
        return this.api.rollingWindowTicker(requestParameters);
    }

    /**
     * 24hr rolling window ticker statistics for a single symbol. These are NOT
     * the statistics of the UTC day, but a 24hr rolling window for the
     * previous 24hrs.
     *
     * Update Speed: 1000ms
     *
     * @summary Individual Symbol Ticker Streams
     * @param {TickerRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<TickerResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#ticker Binance API Documentation}
     */
    ticker(requestParameters: TickerRequest): WebsocketStream<TickerResponse> {
        return this.api.ticker(requestParameters);
    }

    /**
     * The Trade Streams push raw trade information; each trade has a unique
     * buyer and seller.
     *
     * Update Speed: Real-time
     *
     * @summary Trade Streams
     * @param {TradeRequest} requestParameters Request parameters.
     *
     * @returns {WebsocketStream<TradeResponse>}
     * @throws {RequiredError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-streams/~#trade Binance API Documentation}
     */
    trade(requestParameters: TradeRequest): WebsocketStream<TradeResponse> {
        return this.api.trade(requestParameters);
    }
}
